<template>
  <div>
    <!-- Mobile: compact list row, no image -->
    <div
      class="d-md-none mobile-list-item c-pointer"
      :class="{ 'mobile-list-item--selected': selected }"
      @click="goToView()"
    >
      <b-form-checkbox
        v-if="selectable"
        v-model="selected"
        @input="
          selected ? $emit('checkFile', file.id) : $emit('uncheckFile', file.id)
        "
        @click.native.stop
      />
      <div class="mobile-list-item__info">
        <span class="mobile-list-item__name">{{ fileName }}</span>
        <span class="mobile-list-item__meta">
          <template v-if="attributionSamePerson">
            Created &amp; owned by {{ ownedByLabel }}
          </template>
          <template v-else> Created by {{ createdByLabel }} </template>
          <span class="mx-1">|</span>
          <template v-if="showVisibilityInList"
            >{{ fileVisibilityDisplay }}&nbsp;|&nbsp;</template
          >
          {{ uploaded }}
          <template v-if="shareRecipientEmail">
            <span class="mx-1">|</span> Shared with {{ shareRecipientEmail }}
          </template>
        </span>
      </div>
      <file-options
        v-if="file || file.file"
        :file="file.file ? file.file : file"
        :is-requests-file-page="isRequestsFilesPage"
        :shared-view="sharedView"
        :can-reshare="canReshare"
        :share-recipient-email="shareRecipientEmail"
        @showAlert="showAlert"
        @shareFile="shareFile"
        @deletedFile="deletedFile"
      />
    </div>

    <!-- Desktop: card with image -->
    <b-card
      class="d-none d-md-block c-pointer"
      :class="{ hovered: hover || selected }"
      @mouseover="hover = true"
      @mouseleave="hover = false"
    >
      <div v-show="hover || selected" class="space-hover">
        <file-options
          class="kebab"
          v-if="file || file.file"
          :file="file.file ? file.file : file"
          :is-requests-file-page="isRequestsFilesPage"
          :shared-view="sharedView"
          :can-reshare="canReshare"
          :share-recipient-email="shareRecipientEmail"
          @showAlert="showAlert"
          @shareFile="shareFile"
          @deletedFile="deletedFile"
        ></file-options>
        <b-form-checkbox
          v-if="selectable"
          class="left"
          v-model="selected"
          @input="
            selected
              ? $emit('checkFile', file.id)
              : $emit('uncheckFile', file.id)
          "
        ></b-form-checkbox>
      </div>
      <b-card-body
        v-show="hover || selected"
        class="card-hovered"
        @click="goToView()"
      >
        <div class="card-details-container--hovered">
          <b-card-title>
            <h5 class="font-weight-bold">{{ fileName }}</h5>
          </b-card-title>
          <div class="d-flex w-100 just1ify-content-between">
            <div>
              <b-card-sub-title class="mb-2 p-0 border-0">
                <div class="file-attribution">
                  <div class="file-attribution__line">
                    <span class="file-attribution__label">Created by</span>
                    <span class="file-attribution__name">{{
                      createdByLabel
                    }}</span>
                  </div>
                  <div class="file-attribution__line">
                    <span class="file-attribution__label">Owned by</span>
                    <span class="file-attribution__name">{{
                      ownedByLabel
                    }}</span>
                  </div>
                  <div
                    class="file-attribution__line"
                    v-if="shareRecipientEmail"
                  >
                    <span class="file-attribution__label">Shared with</span>
                    <span class="file-attribution__name">{{
                      shareRecipientEmail
                    }}</span>
                  </div>
                </div>
              </b-card-sub-title>
              <b-card-sub-title class="small mt-1 gray--3">
                <span v-if="showVisibilityInList">{{
                  fileVisibilityDisplay
                }}</span>
                <span
                  v-if="
                    showVisibilityInList && isShareable && !isBusinessAdminUser
                  "
                  class="mx-1"
                  >|</span
                >
                <span v-show="isShareable && !isBusinessAdminUser">
                  <b-icon icon="eye" class="mr-1"></b-icon>
                  {{ fileSharesCount }} people
                  <span class="mx-1">|</span>
                </span>
                <span
                  v-if="
                    showVisibilityInList &&
                    !(isShareable && !isBusinessAdminUser)
                  "
                  class="mx-1"
                  >|</span
                >
                {{ uploaded }}
                <b-img
                  :src="require('@/assets/icons/pin.svg')"
                  v-if="file.is_pinned"
                  class="pin"
                ></b-img>
              </b-card-sub-title>
            </div>
          </div>
        </div>
        <b-card no-body class="image-container">
          <SpreadsheetThumbPreview
            v-if="isSpreadsheetPreview"
            :file="firstAttachedFile"
          />
          <b-img-lazy
            v-else-if="file || file.files.length"
            class="border-0 rounded-0 image"
            v-bind="mainProps"
            :src="fileSrc"
            @error="onImageError"
          ></b-img-lazy>
        </b-card>
        <b-card-footer v-if="!isBusinessAdminUser" class="px-0 pt-1">
          <h5>
            <b-badge
              pill
              :variant="statusBadge(fileStatus)"
              class="badge-hovered text-capitalize"
            >
              {{ fileStatus }}
            </b-badge>
            <b-badge
              v-if="pendingDeletionLabel"
              pill
              variant="danger"
              class="ml-1"
              data-testid="file-card-pending-deletion"
            >
              {{ pendingDeletionLabel }}
            </b-badge>
          </h5>
        </b-card-footer>
      </b-card-body>

      <b-card-body v-show="!hover && !selected" class="p-0">
        <div class="card-details-container">
          <b-card-title>
            <h5 class="font-weight-bold">{{ fileName }}</h5>
          </b-card-title>
          <div class="d-flex w-100 justify-content-between">
            <div>
              <b-card-sub-title class="mb-2 p-0 border-0">
                <div class="file-attribution">
                  <div class="file-attribution__line">
                    <span class="file-attribution__label">Created by</span>
                    <span class="file-attribution__name">{{
                      createdByLabel
                    }}</span>
                  </div>
                  <div class="file-attribution__line">
                    <span class="file-attribution__label">Owned by</span>
                    <span class="file-attribution__name">{{
                      ownedByLabel
                    }}</span>
                  </div>
                  <div
                    class="file-attribution__line"
                    v-if="shareRecipientEmail"
                  >
                    <span class="file-attribution__label">Shared with</span>
                    <span class="file-attribution__name">{{
                      shareRecipientEmail
                    }}</span>
                  </div>
                </div>
              </b-card-sub-title>
              <b-card-sub-title class="small mt-1 gray--3">
                <span v-if="showVisibilityInList">{{
                  fileVisibilityDisplay
                }}</span>
                <span
                  v-if="
                    showVisibilityInList && isShareable && !isBusinessAdminUser
                  "
                  class="mx-1"
                  >|</span
                >
                <span v-show="isShareable && !isBusinessAdminUser">
                  <b-icon icon="eye" class="mr-1"></b-icon>
                  {{ fileSharesCount }} people
                  <span class="mx-1">|</span>
                </span>
                <span
                  v-if="
                    showVisibilityInList &&
                    !(isShareable && !isBusinessAdminUser)
                  "
                  class="mx-1"
                  >|</span
                >
                {{ uploaded }}
                <b-img
                  :src="require('@/assets/icons/pin.svg')"
                  v-if="file.is_pinned"
                  class="pin"
                ></b-img>
              </b-card-sub-title>
            </div>
          </div>
        </div>
        <b-card no-body class="image-container">
          <SpreadsheetThumbPreview
            v-if="isSpreadsheetPreview"
            :file="firstAttachedFile"
          />
          <b-img-lazy
            v-else-if="file || file.files.length"
            class="border-0 rounded-0 image"
            v-bind="mainProps"
            :src="fileSrc"
            @error="onImageError"
          ></b-img-lazy>
        </b-card>
        <b-card-footer v-if="!isBusinessAdminUser" class="px-0 pt-1">
          <h5>
            <b-badge
              pill
              :variant="statusBadge(fileStatus)"
              class="badge-hovered text-capitalize"
            >
              {{ fileStatus }}
            </b-badge>
            <b-badge
              v-if="pendingDeletionLabel"
              pill
              variant="danger"
              class="ml-1"
              data-testid="file-card-pending-deletion"
            >
              {{ pendingDeletionLabel }}
            </b-badge>
          </h5>
        </b-card-footer>
      </b-card-body>
    </b-card>

    <b-alert
      class="custom-alert"
      :show="dismissCountDown"
      dismissible
      fade
      variant="primary"
      @dismiss-count-down="countDownChanged"
      >File has been {{ alertMsg }}.</b-alert
    >
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import FileOptions from '@/components/modals/FileOptions'
import { parseServerDate } from '@/utils/dateHelper'
import {
  getCreatedByLabel,
  getOwnedByLabel,
  isSameCreatorAndOwner,
  formatFileVisibilityDisplay,
  FILE_VISIBILITY_PLACEHOLDER,
} from '@/utils/fileLabels'
import { nonImageDocIcon, isCsvFile, isXlsxFile } from '@/utils/fileTypeIcons'
import SpreadsheetThumbPreview from '@/components/files/SpreadsheetThumbPreview'

const DEFAULT_FILE_ICON = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%230066FF' class='bi bi-file-earmark' viewBox='0 0 16 16'%3E%3Cpath d='M14 4.5V14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h5.5L14 4.5zm-3 0A1.5 1.5 0 0 1 9.5 3V1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V4.5h-2z'/%3E%3C/svg%3E`

export default {
  components: {
    FileOptions,
    SpreadsheetThumbPreview,
  },

  props: {
    file: {
      type: Object,
      default: null,
    },
    /** Optional (id) => name from parent list index for original_creator_id */
    resolveCreatorName: {
      type: Function,
      default: null,
    },
    selectedFiles: {
      type: Array,
    },
    /** When false, the selection checkbox is hidden (view-only access). */
    selectable: {
      type: Boolean,
      default: true,
    },
    isRequestsFilesPage: {
      type: Boolean,
      default: false,
    },
    /** When true, per-card options are limited to Download (you don't own these files). */
    sharedView: {
      type: Boolean,
      default: false,
    },
    canReshare: {
      type: Boolean,
      default: false,
    },
    shareRecipientEmail: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      hover: false,
      mainProps: {
        center: true,
        width: 'auto',
      },
      selected: false,
      dismissCountDown: 0,
      alertMsg: '',
      imgSrc: null,
    }
  },

  computed: {
    ...mapGetters('auth', ['user']),
    isBusinessAdminUser() {
      return this.user ? this.user.user_type === 'business_admin' : false
    },
    uploaded() {
      return this.getTimeDiff(this.file.updated_at)
    },
    // Same "which file record actually applies" resolution the home-page
    // widget vs. everywhere-else split has always needed — shared here so
    // fileSrc/firstAttachedFile/isSpreadsheetPreview don't each repeat it.
    resolvedFile() {
      return this.$route.name === 'home.welcome-page' &&
        this.isBusinessAdminUser
        ? this.file.file
        : this.file
        ? this.file.file
          ? this.file.file
          : this.file
        : this.file
    },
    firstAttachedFile() {
      const file = this.resolvedFile
      if (!file || !file.files || !file.files.length) return null
      return file.files[0]
    },
    isSpreadsheetPreview() {
      return (
        isCsvFile(this.firstAttachedFile) || isXlsxFile(this.firstAttachedFile)
      )
    },
    fileSrc() {
      if (!this.firstAttachedFile) {
        return DEFAULT_FILE_ICON
      }

      // CSV/XLSX never get a real thumb_url — spatie/medialibrary can
      // rasterize an image or a PDF's first page, but there's no
      // conversion path for a spreadsheet, so thumb_url is missing/broken
      // for these. Render the bundled icon directly instead of letting the
      // <img> fail and fall back to onImageError's generic icon. (CSV/XLSX
      // are handled separately — see isSpreadsheetPreview/
      // SpreadsheetThumbPreview — this branch is only reached as a
      // fallback if that component itself falls back to its own icon.)
      const docIcon = nonImageDocIcon(this.firstAttachedFile)
      if (docIcon) return docIcon

      return this.firstAttachedFile.thumb_url
    },
    fileSharesCount() {
      return this.file.file_shares ? this.file.file_shares.length : 0
    },
    // Set while a tradie-deleted file shared with a company sits in its notice period.
    pendingDeletionLabel() {
      const at = this.file && this.file.deletion_scheduled_at
      if (!at) return ''
      const d = parseServerDate(at)
      return d
        ? `Deleting ${d.toLocaleDateString('en-AU', {
            day: 'numeric',
            month: 'short',
          })}`
        : 'Pending deletion'
    },
    fileStatus() {
      return this.file && !this.file.file
        ? this.file.status
            .trim()
            .toLowerCase()
            .replace(/\w\S*/g, (w) => w.replace(/^\w/, (c) => c.toUpperCase()))
        : this.file && this.file.file
        ? this.file.file.status
            .trim()
            .toLowerCase()
            .replace(/\w\S*/g, (w) => w.replace(/^\w/, (c) => c.toUpperCase()))
        : ''
    },
    isShareable() {
      return (
        this.file.status === 'valid' ||
        this.file.status === 'not started' ||
        this.file.status === 'expiring soon'
      )
    },

    fileName() {
      return this.file.name || (this.file.file && this.file.file.name) || ''
    },
    ownedByLabel() {
      return getOwnedByLabel(this.file)
    },
    createdByLabel() {
      return getCreatedByLabel(this.file, (id) => this.creatorNameChain(id))
    },
    attributionSamePerson() {
      return isSameCreatorAndOwner(this.file, (id) => this.creatorNameChain(id))
    },
    fileVisibilityDisplay() {
      return formatFileVisibilityDisplay(this.file)
    },
    showVisibilityInList() {
      return this.fileVisibilityDisplay !== FILE_VISIBILITY_PLACEHOLDER
    },
  },

  methods: {
    creatorNameChain(id) {
      if (this.resolveCreatorName) {
        const fromList = this.resolveCreatorName(id)
        if (fromList && String(fromList).trim()) return String(fromList).trim()
      }
      if (this.user && this.user.id === id) {
        return (this.user.full_name || this.user.email || '').trim() || null
      }
      const u =
        (this.file && this.file.user) ||
        (this.file && this.file.file && this.file.file.user)
      if (u && Number(u.id) === Number(id)) {
        return (u.full_name || u.email || '').trim() || null
      }
      return null
    },
    onImageError(e) {
      e.target.src = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='64' height='64' fill='%230066FF' class='bi bi-file-earmark-pdf' viewBox='0 0 16 16'%3E%3Cpath d='M14 4.5V14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h5.5L14 4.5zm-3 0A1.5 1.5 0 0 1 9.5 3V1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V4.5h-2z'/%3E%3C/svg%3E`
    },
    goToView() {
      if (this.isRequestsFilesPage) {
        this.$router.replace({
          name: 'request.view-file-page',
          params: { id: this.file.id },
          // Carry the template-name filter through so "Back" on the file
          // view returns to the same filtered list instead of "all files".
          query: { name: this.$route.query.name },
        })
      } else {
        const id = this.file.file_id ? this.file.file_id : this.file.id
        const query =
          this.$route.name === 'file.list-page' ? { from: 'file-list' } : {}

        if (this.sharedView) {
          query.shared = '1'
        }

        this.$router.replace({
          name: 'file.view-page',
          params: { id },
          query,
        })
      }
    },
    showAlert(action) {
      this.dismissCountDown = 1
      this.alertMsg = action
    },
    countDownChanged(dismissCountDown) {
      this.dismissCountDown = dismissCountDown
    },
    shareFile() {
      this.$emit('shareFile', this.file)
    },
    deletedFile() {
      this.$emit('deletedFile', this.file)
    },
    statusBadge(status) {
      return status === 'Valid'
        ? 'success'
        : status === 'Pending Approval'
        ? 'warning'
        : status === 'Rejected'
        ? 'danger'
        : 'secondary'
    },
  },

  watch: {
    selectedFiles(newVal, oldVal) {
      const index = newVal.findIndex((f) => f === this.file.id)

      this.selected = !(index < 0)
    },
  },
}
</script>

<style lang="scss" scoped>
.card {
  width: 342px;
  height: auto;
}

.card-details-container {
  &--hovered {
    padding-bottom: 0.25rem;
  }

  background-color: white;
  padding-bottom: 0.75rem;
}

.card-body {
  padding: 1.5rem;
  /* Bootstrap card-body defaults to flex-grow; avoid stretching the inner box to fill .card */
  flex: 0 0 auto;
}

.card-footer {
  border: 0;
  background: transparent;
}

.image-container {
  width: 100%;
  height: 200px;
  border: 2px solid #e1e6ea;
  border-radius: 8px;
}

.image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.pin {
  width: 16px;
  height: 16px;
}

.left {
  // Was position:absolute top:0, matched against .kebab's own top:0 — but
  // the checkbox's visual box (centered on its label via the global
  // .custom-checkbox rule) and the kebab button's icon (centered in its own
  // padded box) don't share the same intrinsic height, so two independent
  // top:0 anchors didn't land on the same line. .space-hover now centers
  // both children on one flex row instead (see below); `order` puts this
  // one first (left).
  order: 1;
  // Was 1rem (matching .card-hovered's own padding-left, lining the
  // checkbox up with the title/image-container's left edge below it) —
  // nudged in a bit closer to the card's edge instead.
  margin-left: 0.5rem;
  // .space-hover centers both items on the flex row by their own box
  // height — but the checkbox's box (its label's line-height) is taller
  // than the kebab button's tight padded box, so "centered on its own box"
  // still landed the two on different lines. Rather than nudge by a
  // guessed pixel amount, give this the same explicit height as .kebab
  // (24px — the checkbox's own visual box size, see
  // .custom-control-label::before/after below) and center its content
  // inside that, so both are centered flex items of identical height.
  display: flex;
  align-items: center;
  height: 24px;
}

.custom-control-label::before {
  width: 24px;
  height: 24px;
  background-color: #d0e4ff !important;
  border: 2px solid #d0e4ff !important;
}

.kebab {
  // Sits on the same flex row as .left (see .space-hover) instead of its
  // own independent top:0 anchor — the checkbox and the dropdown toggle
  // don't share the same intrinsic height, so two separate top:0 anchors
  // never actually lined up their visible centers. `order` puts this one
  // second (right).
  //
  // Verified against the running app: without an explicit `position` here,
  // this silently inherits the *global* `.kebab { position: absolute; top:
  // 20px; right: 20px; }` rule in main.scss (this div's own class list
  // literally includes "kebab", same as the icon-collision case handled
  // below) — which yanks it out of .space-hover's flex row entirely and
  // re-anchors it against the outer .card instead, undoing the row's
  // alignment. Force it back to a normal flex item.
  position: static;
  order: 2;
  margin-right: 1rem;
  // Match .left's explicit height so both flex items in .space-hover center
  // on identical box heights instead of each keeping its own intrinsic one.
  display: flex;
  align-items: center;
  height: 24px;

  // The dropdown toggle is a global .btn (height: 56px; padding: 4px 32px —
  // see _button.scss), which pushes the "..." icon well down and in from
  // the button's own edge instead of sitting flush in the corner like the
  // checkbox does. Hug the icon instead, same fix as
  // TimesheetListTab.vue's .action-menu .kebab.
  /deep/ .btn {
    height: auto;
    min-height: 0;
    padding: 0.25rem;
    line-height: 1;
    // The three-dots icon sizes itself at 1em off the button's inherited
    // font-size (a card-context heading size, ~20px), rendering larger than
    // the checkbox next to it. Shrink the font-size so the icon (and the
    // button's own padding-driven footprint) scale down together.
    font-size: 0.8rem;
  }

  // FileOptions.vue's dropdown-menu offset (top: -15px; left: -20px) was a
  // fixed pixel fudge meant to nudge the menu against the old 56px-tall
  // button. b-dropdown positions its menu via Popper.js though, which sets
  // its own top:0/left:0 (no !important) and does the real placement with
  // an inline `transform: translate3d(...)` computed fresh from the
  // toggle's actual on-screen position — so any !important top/left here
  // doesn't relocate the menu, it just adds an extra, constant offset on
  // top of wherever Popper already put it. That's what was dragging the
  // menu away from the kebab. Popper already aligns the menu correctly
  // (bottom-end, flush under/against the toggle) via b-dropdown's `right`
  // prop in FileOptions.vue — so the fix is to zero the offset back out
  // for this instance, not to invent a new pixel fudge. Scoped to .kebab
  // so the mobile file-options instance (which keeps the full-size button,
  // where the old fudge is still in play) is unaffected.
  /deep/ .dropdown-menu {
    top: 0 !important;
    left: 0 !important;
  }

  // FileOptions.vue's own three-dots icon is *also* named "kebab"
  // (<b-icon class="kebab">), which collides with the unrelated global
  // `.kebab { position: absolute; top: 20px; right: 20px; }` rule in
  // main.scss. That rule was shoving the dot icon 20px down/right inside
  // the now-hugging button, which is what left it sitting below the
  // checkbox instead of level with it. Neutralize it the same way
  // TimesheetListTab.vue's `.action-menu .kebab` does.
  /deep/ .kebab {
    position: static;
    top: auto;
    right: auto;
    width: auto;
    height: auto;
  }
}

/deep/ .badge-hovered {
  padding: 5px 20px 5px 20px;
  background: #dadde2 0% 0% no-repeat padding-box;
  color: #3f4858 !important;

  &.badge-success {
    background: #d1ffef 0% 0% no-repeat padding-box !important;
    color: #00d392 !important;
  }

  &.badge-danger {
    background: #ffe2e2 0% 0% no-repeat padding-box !important;
    color: #e52c00 !important;
  }

  &.badge-warning {
    background: #fff3cc 0% 0% no-repeat padding-box !important;
    color: #a07602 !important;
  }
}

.hovered {
  background: #eaf3ff 0% 0% no-repeat padding-box;
  box-shadow: 0px 10px 25px #00429d0d;
}

.card-hovered {
  width: 100%;
  min-height: auto;
  height: auto;
  overflow: visible;
  border-radius: 8px;
  opacity: 1;
  background: #ffffff 0% 0% no-repeat padding-box;
  box-shadow: 0px 10px 25px #00429d0d;
  border: 2px solid #006cff;
  padding-top: 1rem;
  padding-left: 1rem;
  padding-right: 1rem;
  padding-bottom: 0.5rem;
  box-sizing: border-box;
}

.space-hover {
  // .kebab and .left (the checkbox) used to be independently
  // position:absolute, each anchored to top:0 — but the checkbox's visual
  // box and the dropdown toggle's icon don't share the same intrinsic
  // height, so their two top:0 anchors didn't land on the same visible
  // line (the checkbox rendered noticeably higher than the kebab). A flex
  // row with align-items:center guarantees both sit on one shared
  // centerline regardless of that height difference; `order` on the two
  // children (see .left/.kebab) keeps the checkbox on the left and the
  // kebab on the right despite the kebab coming first in the markup.
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  // Was a flat `min-height: 2.5rem` centering the 24px row, which split
  // its spare space evenly (~0.5rem) above *and* below. The gap below
  // (between this row and .card-hovered's "Passport" box) needs to stay
  // at that original ~0.5rem, but the gap above (between the outer card's
  // top edge and the row) was excessive — so size the two independently
  // instead of relying on a symmetric min-height.
  padding-top: 0.25rem;
  padding-bottom: 0.5rem;
}

.file-attribution {
  text-align: left;
}

.file-attribution__line {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  column-gap: 0.35rem;
  row-gap: 0.1rem;
  line-height: 1.3;
  font-size: 0.8125rem;

  & + .file-attribution__line {
    margin-top: 0.2rem;
  }
}

.file-attribution__label {
  color: #8b95a3;
  font-weight: 500;
  font-size: 0.72rem;
  letter-spacing: 0.01em;
}

.file-attribution__name {
  color: #3f4858;
  font-weight: 600;
}

/* Mobile list row (hidden on md+) */
.mobile-list-item {
  display: flex;
  align-items: center;
  padding: 0.65rem 0.75rem;
  background: white;
  border-bottom: 1px solid #e1e6ea;
  gap: 0.6rem;
  // Name and meta may wrap to a second line (clamped below) so the wording
  // fits on narrow phones; min-height keeps short rows from collapsing so
  // the list still reads evenly.
  min-height: 3.25rem;

  &--selected {
    background: #eaf3ff;
  }

  &__info {
    flex: 1;
    min-width: 0;
  }

  &__name {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    font-weight: 600;
    font-size: 0.875rem;
    color: #364052;
    overflow: hidden;
    word-break: break-word;
  }

  &__meta {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    font-size: 0.72rem;
    color: #8b95a3;
    margin-top: 0.15rem;
    line-height: 1.4;
    overflow: hidden;
    word-break: break-word;
  }

  // Center the Bootstrap checkbox box within the flex row
  /deep/ .custom-control {
    margin-bottom: 0;
    min-height: 0;
  }

  /deep/ .custom-control-label::before,
  /deep/ .custom-control-label::after {
    top: 50%;
    transform: translateY(-50%);
  }
}
</style>
